import React, { useEffect, useState } from 'react';
import { Spin, List, Empty } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import useQuestions from '../../queries/useQuestions';
import ListItem from './components/ListItem/ListItem';
import {
  setImportantQuestionsFirst,
  setQuestionsSortData,
  setSelectedQuestion,
} from '../../store/actions';
import {
  answerStatusSelector,
  closeQuestionRejectedSelector,
  importantQuestionsFirstSelector,
  notificationQuestionSelector,
  questionReactionSelector,
  sortDataSelector,
} from '../../store/selectors';
import { filterQuestionsFns } from '../../utils/filterQuestionsFns';
import { questionsSortStates } from '../../constants/common';
import SortDropdown from '../SortDropdown/SortDropdown';
import PriorityFirstSwitch from '../../../../components/PriorityFirstSwitch/PriorityFirstSwitch';
import { stableSort } from '../../../../helpers/sortFns';
import styles from './QuestionsList.module.scss';

const QuestionsList = ({ className, queryParams }) => {
  const dispatch = useDispatch();

  const answerStatus = useSelector(answerStatusSelector);
  const closeQuestionRejected = useSelector(closeQuestionRejectedSelector);
  const sortData = useSelector(sortDataSelector);
  const importantFirst = useSelector(importantQuestionsFirstSelector);
  const reaction = useSelector(questionReactionSelector);
  const notificationQuestion = useSelector(notificationQuestionSelector);

  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [sortedQuestions, setSortedQuestions] = useState([]);

  const { data: questions, isFetching: isQuestionsFetching } = useQuestions(
    notificationQuestion
      ? { questionNum: Number(notificationQuestion.id) }
      : queryParams,
    { enabled: Boolean(notificationQuestion?.id || queryParams) }
  );

  useEffect(() => {
    if (notificationQuestion && questions) {
      const question = questions.find(
        (item) => item.id === Number(notificationQuestion.id)
      );

      if (question) {
        dispatch(setSelectedQuestion(question));
      }
    }
  }, [notificationQuestion?.id, questions]);

  const setSortData = (sortData) => {
    dispatch(setQuestionsSortData(sortData));
  };

  const handleImportantFirstChange = (checked) => {
    dispatch(setImportantQuestionsFirst(checked));
  };

  useEffect(() => {
    let newQuestions = [];

    if (questions?.length) {
      const questionsByAnswerStatus = filterQuestionsFns.byAnswerStatus(
        questions,
        answerStatus
      );
      newQuestions = filterQuestionsFns.byReaction(
        questionsByAnswerStatus,
        reaction
      );

      if (closeQuestionRejected) {
        newQuestions = filterQuestionsFns.byRejected(newQuestions);
      }
    }

    setFilteredQuestions(newQuestions);
  }, [questions, answerStatus, closeQuestionRejected, reaction]);

  useEffect(() => {
    const { content, value, ...restSortData } = sortData;

    let newQuestions = [];
    if (importantFirst) {
      const arrs = filteredQuestions.reduce(
        (acc, question) => {
          if (question.isImportant) {
            acc.important.push(question);
          } else {
            acc.notImportant.push(question);
          }

          return acc;
        },
        {
          important: [],
          notImportant: [],
        }
      );

      const importantQuestions = stableSort({
        arr: arrs.important,
        ...restSortData,
      });
      const notImportantQuestions = stableSort({
        arr: arrs.notImportant,
        ...restSortData,
      });

      newQuestions = importantQuestions.concat(notImportantQuestions);
    } else {
      newQuestions = stableSort({
        arr: filteredQuestions,
        ...restSortData,
      });
    }

    setSortedQuestions(newQuestions);
  }, [filteredQuestions, sortData, importantFirst]);

  return (
    <Spin
      wrapperClassName={className}
      size="large"
      spinning={isQuestionsFetching}
      tip="Загрузка..."
    >
      {Boolean(sortedQuestions.length) ? (
        <>
          <div className={styles.settingsContainer}>
            <PriorityFirstSwitch
              checked={importantFirst}
              onClick={handleImportantFirstChange}
              content="Сначала важные"
            />
            <SortDropdown
              btnClassName={styles.dropdownBtn}
              sortData={sortData}
              items={Object.values(questionsSortStates)}
              setSortData={setSortData}
            />
          </div>
          <List
            itemLayout="vertical"
            dataSource={sortedQuestions}
            renderItem={(item) => <ListItem item={item} />}
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Вопросы отсутствуют"
        />
      )}
    </Spin>
  );
};

export default QuestionsList;
